import React from 'react';
import { Edit, Power, PowerOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import type { FiscalParameter } from '../types/system-parameters.types';

interface FiscalParametersTableProps {
  parameters: FiscalParameter[];
  onEdit: (parameter: FiscalParameter) => void;
  onDelete?: (parameter: FiscalParameter) => void;
}

type SortField = 'name' | 'code' | 'value' | 'updatedAt';

export const FiscalParametersTable: React.FC<FiscalParametersTableProps> = ({
  parameters,
  onEdit,
  onDelete
}) => {
  const fieldLabels: Record<SortField, string> = {
    name: 'Parámetro',
    code: 'Código',
    value: 'Valor',
    updatedAt: 'Última Actualización'
  };

  const sortValueExtractor = (parameter: FiscalParameter, field: SortField): any => {
    switch (field) {
      case 'name':
        return parameter.name.toLowerCase();
      case 'code':
        return parameter.code.toLowerCase();
      case 'value':
        return parseFloat(parameter.value) || 0;
      case 'updatedAt':
        return new Date(parameter.updatedAt);
      default:
        return parameter.name;
    }
  };

  const tableData = useTableData<FiscalParameter, SortField>(
    {
      data: parameters,
      defaultSortField: 'name',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (parameters.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay parámetros fiscales</h3>
        <p className="text-gray-500">No se encontraron parámetros con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Parámetro
              </SortableHeader>
              <SortableHeader
                field="code"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Código
              </SortableHeader>
              <SortableHeader
                field="value"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-right"
              >
                Valor
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <SortableHeader
                field="updatedAt"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Última Act.
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((parameter) => (
              <tr key={parameter.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {parameter.name}
                    </div>
                    {parameter.description && (
                      <div className="text-xs text-gray-500 mt-1">
                        {parameter.description}
                      </div>
                    )}
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div className="text-sm font-mono text-gray-600">
                    {parameter.code}
                  </div>
                </td>

                <td className="px-4 py-4 text-right">
                  <div className="text-sm font-semibold text-gray-900">
                    {parameter.type === 'percentage' ? `${parameter.value}%` : `S/ ${parameter.value}`}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge className={parameter.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                    {parameter.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="text-xs text-gray-600">
                    {new Date(parameter.updatedAt).toLocaleDateString('es-PE')}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(parameter)}
                      className="text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    {onDelete && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(parameter)}
                        className={`text-xs ${parameter.isActive ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                      >
                        {parameter.isActive ? (
                          <>
                            <PowerOff className="w-3 h-3 mr-1" />
                            Desactivar
                          </>
                        ) : (
                          <>
                            <Power className="w-3 h-3 mr-1" />
                            Reactivar
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};
